import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Input,
  Modal,
  Row,
  TablePaginationConfig,
  Tooltip,
  Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DerivedTeamRoster } from '../../hooks/types';
import useCreateTeamRoleAssignments from '../../hooks/useCreateTeamRoleAssignment';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import { useNotify } from '../../hooks/useNotify';
import { usePermission } from '../../hooks/usePermission';
import useQueryFirstShift from '../../hooks/useQueryFirstShift';
import useQueryTeamRoles from '../../hooks/useQueryTeamRoles';
import { queryTeamRosterName } from '../../hooks/useQueryTeamRoster';
import { onApolloError } from '../../lib/graphql/helpers';
import { shiftBackgroundColours } from '../../theme';
import routes, { routesWithPathParameter } from '../App/routes';
import ExportRosterAsPDFModal from '../DailyRoster/DailyRosterExport';
import PageLoadingSpinner from '../PageLoadingSpinner';
import { Optional } from '../Shared';
import TeamRoleInput from '../Teams/TeamEditor/TeamRoleInput';
import { defaultDayShiftPeriod } from '../Teams/TeamEditor/util';
import ExportShiftManager from '../Teams/TeamRoster/ExportShiftManager';
import { recordStatusSchema } from '../Teams/types';
import RosterTable from './RosterTable';
import TimeDisplay from './TimeDisplay';
import { getRoleInformation } from './lib/prioritised-roles';
import { columnHeaders, shiftRecords } from './lib/util';
import type {
  RoleInformation,
  RosterColumnHeader,
  RosterData,
  RosterDisplayMode,
  RosterTableRowRecord,
  ShiftPeriod,
} from './types';

type RosterProps = {
  /**
   * The team ID and rosters to be displayed. These will be filtered by user id, if a user id is given
   */
  readonly teamRosters: readonly DerivedTeamRoster[];
  /**
   * Whether external data is still loading; this will prevent the scheduler from being displayed.
   */
  readonly loading: boolean;
  /**
   * Displays the roster slightly differently if only a single user's schedule is being displayed
   */
  readonly personalRosterMode?: boolean;
  /**
   * Dates to render for the roster.
   */
  readonly rosterDates: readonly Date[];
  /**
   * Roster's display mode: monthly or weekly.
   */
  readonly rosterDisplayMode: RosterDisplayMode;
  /**
   * Roster's table header offset for top, used to change how sticky header behave.
   */
  readonly tableHeaderOffset?: number;
  /**
   * If the roster should allow the user to export data
   */
  readonly allowExport?: boolean;
  /**
   * If the roster should be readonly
   */
  readonly readOnly: boolean;
};

const hiddenRoles: readonly string[] = ['PRAS Admin'];

/**
 * Hides certain roles from the roster.
 * @param record shift record with role information.
 * @returns true if it can be displayed, otherwise false.
 */
const rolesToDisplay = (record: RosterTableRowRecord) =>
  !hiddenRoles.includes(record.roleName);

const { Title } = Typography;

const defaultPageSize = 50;

const availablePageSize: readonly number[] = [5, 15, 30, 50, 100];

/**
 * Renders a roster scheduler that displays and manages the roster information for a Team's roles and users.
 * @param props roster component props.
 * @returns The team's roster
 */
const Roster = ({
  tableHeaderOffset,
  teamRosters,
  loading,
  rosterDates,
  rosterDisplayMode,
  personalRosterMode = false,
  allowExport = false,
  readOnly,
}: RosterProps) => {
  const { notify } = useNotify();
  const [showExportModal, setShowExportModal] = useState(false);

  const toggleExportModal = () => {
    setShowExportModal(!showExportModal);
  };
  const defaultStartTime = rosterDates[0] ?? new Date();

  const [exportDefaultStart, setExportDefaultStart] =
    useState<Date>(defaultStartTime);

  const { refetch: refetchFirstShiftDate } = useQueryFirstShift(
    teamRosters[0]?.teamId,
    onApolloError(notify.error),
    (startTime) => {
      setExportDefaultStart(
        startTime !== undefined ? new Date(startTime) : defaultStartTime
      );
    }
  );

  useEffect(() => {
    void refetchFirstShiftDate();
  }, [refetchFirstShiftDate]);

  const [showRoleModal, setShowRoleModal] = useState(false);

  const [showShiftStatus, setShowShiftStatus] = useState(false);

  const [roleFormValue, setRoleFormValue] = useState<string | undefined>(
    undefined
  );

  const [rosterPageSize, setRosterPageSize] = useState(defaultPageSize);

  const [teamRoleFilter, setTeamRoleFilter] = useState('');

  const navigate = useNavigate();

  const { loggedInUser } = useLoggedInUser();

  const permission = usePermission(teamRosters[0]?.teamId, loggedInUser);

  const [insertRole] = useCreateTeamRoleAssignments(
    onApolloError(notify.error)
  );

  const { data: teamRoleQueryResult } = useQueryTeamRoles(
    onApolloError(notify.error),
    undefined,
    true
  );

  const [rolesInfo, setRolesInfo] = useState<readonly RoleInformation[]>(
    getRoleInformation(teamRoleQueryResult?.team_role ?? [])
  );

  useEffect(() => {
    const roleInformation = getRoleInformation(
      teamRoleQueryResult?.team_role ?? []
    );

    setRolesInfo(roleInformation);
  }, [teamRoleQueryResult]);

  // A collection of all the column headers and shifts within the given rosters
  const rosterData: RosterData = teamRosters.map((roster) => {
    const rosterColumnHeaders: readonly RosterColumnHeader[] = columnHeaders({
      teamId: roster.teamId,
      rosterDates,
      personalRosterMode,
      teamName: personalRosterMode ? roster.teamName : undefined,
      isArchived: roster.teamRecordStatus === recordStatusSchema.Values.Deleted,
    });

    const rosterShiftsRecords: readonly RosterTableRowRecord[] = shiftRecords(
      roster,
      personalRosterMode,
      rolesInfo
    )
      .filter(rolesToDisplay)
      .filter((role) =>
        role.roleName.toLowerCase().includes(teamRoleFilter.toLowerCase())
      );

    const dayShiftPeriod: ShiftPeriod | undefined = roster.dayShiftPeriod;

    return {
      teamId: roster.teamId,
      internalOrderNumber: roster.internalOrderNumber,
      rosterShiftsRecords,
      rosterColumnHeaders,
      dayShiftPeriod,
    };
  });

  const manageTeamLabel = permission.teamManageRosterAndTeam
    ? 'Configure Team'
    : 'View Team';

  const paginationSetting: TablePaginationConfig = {
    pageSize: rosterPageSize,
    showSizeChanger: false,
    position: ['topRight'],
  };

  const rosterPaginationSetting = personalRosterMode
    ? false
    : paginationSetting;

  const onSaveNewRole = useCallback(() => {
    // Since the editing of a team's roster has been implemented here such that only a single team
    // is shown on the page at a time, we assume here that the team id of the first roster
    // is the team to which the role should be assigned.
    //
    // If this component will later be used to add a role to one of several possible teams,
    // this should be extended to provide the exact teamId that is necessary.
    if (loggedInUser !== undefined && teamRosters.length === 1) {
      void insertRole({
        variables: {
          team_role_id: roleFormValue,
          team_id: teamRosters[0]?.teamId,
        },
        refetchQueries: [queryTeamRosterName],
      });

      setShowRoleModal(false);
      setRoleFormValue(undefined);
    }
  }, [insertRole, loggedInUser, roleFormValue, teamRosters]);

  if ((loading && rosterData.length === 0) || loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  // Disable the PDF export option for SOC team
  const disableExportPdfOption = teamRosters[0]?.teamType === 'SOC';

  // If the current user have permission to assign team role to this team.
  const assignTeamRolePermission = permission.teamAssignTeamRole;

  return (
    <>
      <Row gutter={[10, 10]}>
        <Optional show={teamRosters.length > 0 && !personalRosterMode}>
          <Col span={12}>
            <Optional show={assignTeamRolePermission && !readOnly}>
              <Button
                id="assign-role-btn"
                className="add-role-btn"
                onClick={() => setShowRoleModal(true)}
              >
                <PlusOutlined /> Assign Role to Team
              </Button>
            </Optional>
          </Col>

          <Col span={12}>
            <Optional show={!readOnly}>
              <Button
                className="manage-team-btn"
                style={{ float: 'right', minWidth: '120px' }}
                type="primary"
                onClick={() =>
                  navigate(
                    routesWithPathParameter(
                      routes.editTeam,
                      'teamId',
                      teamRosters[0]?.teamId ?? ''
                    )
                  )
                }
              >
                {manageTeamLabel}
              </Button>
            </Optional>
            <Optional show={allowExport}>
              <Dropdown
                menu={{
                  items: [
                    disableExportPdfOption
                      ? null
                      : {
                          key: '1',
                          label: (
                            <Optional show={teamRosters[0] !== undefined}>
                              {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                              <ExportRosterAsPDFModal team={teamRosters[0]!} />
                            </Optional>
                          ),
                        },
                    {
                      key: '2',
                      label: (
                        <ExportShiftManager
                          teamId={teamRosters[0]?.teamId ?? ''}
                          teamName={teamRosters[0]?.teamName ?? ''}
                          defaultFromDate={exportDefaultStart}
                          defaultToDate={
                            rosterDates[rosterDates.length - 1] ?? new Date()
                          }
                          toggleExportModal={toggleExportModal}
                        />
                      ),
                    },
                  ],
                }}
              >
                <Button
                  style={{
                    float: 'right',
                    minWidth: '120px',
                    marginRight: '10px',
                  }}
                  type="primary"
                >
                  Export
                </Button>
              </Dropdown>
            </Optional>
          </Col>
        </Optional>

        <Col span={24}>
          {rosterData.length === 0 ? (
            <Alert
              message={<em>Nothing scheduled.</em>}
              style={{
                fontSize: 40,
                textAlign: 'center',
                maxWidth: '800px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
              type="success"
            />
          ) : (
            rosterData.map(
              (
                {
                  teamId,
                  costObjectId,
                  rosterShiftsRecords,
                  rosterColumnHeaders,
                  dayShiftPeriod,
                },
                index
              ) => (
                <div key={index}>
                  <Row>
                    <Optional show={personalRosterMode}>
                      <Col span={16}>
                        <Title
                          level={4}
                          style={{ paddingBottom: '5px', paddingTop: '30px' }}
                        >
                          {rosterColumnHeaders[0]?.link}
                        </Title>
                      </Col>
                      <Optional show={costObjectId !== undefined}>
                        <Col span={6} id={`team-${teamId}-io-number`}>
                          <div
                            style={{
                              paddingBottom: '5px',
                              paddingTop: '30px',
                              float: 'right',
                            }}
                          >
                            <span style={{ fontWeight: 'bold' }}>
                              IO Number:
                            </span>{' '}
                            <em>{costObjectId}</em>
                          </div>
                        </Col>
                        <Col span={2} id={`team-${teamId}-io-number-btn`}>
                          <div
                            style={{
                              paddingLeft: '0.25rem',
                              paddingBottom: '5px',
                              paddingTop: '26px',
                            }}
                          >
                            <Tooltip title="Copy IO Number to clipboard">
                              <Button
                                style={{
                                  background: 'transparent',
                                  border: 'transparent',
                                }}
                                aria-label={'Copy IO Number to clipboard'}
                                onClick={() =>
                                  void navigator.clipboard.writeText(
                                    costObjectId ?? ''
                                  )
                                }
                              >
                                <CopyOutlined />
                              </Button>
                            </Tooltip>
                          </div>
                        </Col>
                      </Optional>
                    </Optional>
                    <Col span={24}>
                      <Checkbox
                        id="show-shift-status-checkbox"
                        checked={showShiftStatus}
                        onChange={(e) => setShowShiftStatus(e.target.checked)}
                      >
                        Show shift status
                      </Checkbox>
                      <TimeDisplay
                        dayShiftPeriod={dayShiftPeriod ?? defaultDayShiftPeriod}
                        shiftBackgroundColours={shiftBackgroundColours}
                      />
                    </Col>
                  </Row>

                  <div style={{ paddingBottom: '20px' }}>
                    <Optional show={!personalRosterMode}>
                      <label htmlFor="search-role-input">Search Role</label>
                      <Input
                        id="search-role-input"
                        value={teamRoleFilter}
                        style={{
                          padding: '10px',
                          maxWidth: '250px',
                          marginBottom: '10px',
                          marginLeft: '10px',
                        }}
                        placeholder="Enter role name here"
                        onInput={(e) => {
                          setTeamRoleFilter(e.currentTarget.value);
                        }}
                      />

                      {/* Use customised selector for page size changer, so it is accessible. Antd built in size change is not accessible and cannot be customised */}
                      <select
                        aria-label="page size changer"
                        name="page-size-changer"
                        style={{
                          float: 'right',
                          margin: '10px 0',
                          padding: '8px',
                        }}
                        value={rosterPageSize}
                        onChange={(e) => {
                          setRosterPageSize(parseInt(e.target.value, 10));
                        }}
                      >
                        {availablePageSize.map((pageSize) => (
                          <option value={pageSize} key={pageSize}>
                            Show {pageSize} roles per page
                          </option>
                        ))}
                      </select>
                    </Optional>
                    <RosterTable
                      {...{
                        tableHeaderOffset,
                        loading,
                        rosterShiftsRecords,
                        rosterPaginationSetting,
                        rosterDisplayMode,
                        rosterColumnHeaders,
                        personalRosterMode,
                        dayShiftPeriod,
                        // Give user roster edit permission if user can assign roles under this team's roster
                        manageRosterPermission: assignTeamRolePermission,
                        rolesInfo,
                        readOnly,
                        showShiftStatus,
                      }}
                    />

                    <Divider />
                  </div>
                </div>
              )
            )
          )}
        </Col>
      </Row>

      <Modal
        title="Assign Role"
        open={showRoleModal}
        footer={null}
        destroyOnClose
        onCancel={() => setShowRoleModal(false)}
      >
        <Row wrap={false} style={{ width: '100%' }}>
          <Col flex="auto">
            <TeamRoleInput
              value={roleFormValue}
              onChange={setRoleFormValue}
              teamId={teamRosters[0]?.teamId}
              externalUserId={loggedInUser.external_id}
            />
          </Col>
          <Col flex="60px">
            <Button
              id="save-team-role-input-btn"
              style={{ marginLeft: '10px', height: '40px' }}
              type="primary"
              htmlType="submit"
              onClick={onSaveNewRole}
              disabled={roleFormValue === undefined}
            >
              Save
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default Roster;
